import React, { useState, useEffect } from "react";
import "./review.css";
import { useLocation, useNavigate } from "react-router-dom";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import axios from "axios";

const ReviewComponent = () => {
  const location = useLocation();
  const { claim } = location.state || {};
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [downloadStatus, setDownloadStatus] = useState("");
  const [progressData, setProgressData] = useState({});

  const claimNumber = claim?.claimNumber || "unknown";

  //  Fetch progress data (same as ClaimsList)
  useEffect(() => {
    const fetchProgress = async () => {
      try {
        const res = await axios.post(`/process_data/claim_progress/${claimNumber}`);
        setProgressData(res.data || {});
      } catch (err) {
        console.error("Error fetching claim progress:", err);
      }
    };
    if (claimNumber !== "unknown") fetchProgress();
  }, [claimNumber]);

  //  Same getStep logic as ClaimsList
  const getStep = (progressData) => {
    if (!progressData) return 2;
    let step = 2;

    if (progressData.survey_doc && progressData.survey_upload)
      step = 3;

    if (step === 3 && progressData.work_app_update)
      step = 4;

    if (step === 4 && (progressData.pre_repair_doc || progressData.pre_repair_upload))
      step = 5;

    if (step === 5 && (progressData.post_repair_doc || progressData.post_repair_upload))
      step = 6;

    if (step === 6 && (progressData.invoice || progressData.pre_invoice))
      step = 7;

    if (step === 7 && progressData.delivery)
      step = 9;

    return step;
  };

  const getStatusText = (stepsCompleted, totalSteps) => {
    if (stepsCompleted === 0) return "Claim process not started.";
    if (stepsCompleted === totalSteps)
      return "All steps completed. Claim is ready.";
    return `${stepsCompleted} of ${totalSteps} steps are completed. Finish the steps to complete the claiming process.`;
  };

  const currentStep = getStep(progressData);
  const totalSteps = claim?.totalSteps || 5;
  const progressPercent = (currentStep / totalSteps) * 100;
  const progressLabel = `${currentStep}/${totalSteps}`;

  //  Handle buttons and navigation
  const handlePreviewClick = async (report) => {
    if (report === "Initial Work Report") {
      navigate(`/initial-report/${claimNumber}`);
    } else if (report === "Final Work Report") {
      navigate(`/initial-report-manual/${claimNumber}`);
    } else if (report === "PSU Report") {
      await handlePSUReport(claimNumber);
    } else if (report === "Final Survey Report") {
      handleFinalSurveyReport(claimNumber);
    } else {
      setShowModal(true);
    }
  };

  const handlePSUReport = async (claimNum) => {
    try {
      setDownloadStatus("downloading");
      navigate(`/psu-report/${claimNum}`);
      setTimeout(() => {
        downloadPSUReport(claimNum);
      }, 200);
    } catch (error) {
      console.error("Error:", error);
      setDownloadStatus("error");
      setTimeout(() => setDownloadStatus(""), 3000);
    }
  };

  const handleFinalSurveyReport = (claimNum) => {
    try {
      setDownloadStatus("loading");
      navigate(`/final-survey-report/${claimNum}`);
      setTimeout(() => setDownloadStatus(""), 1000);
    } catch (error) {
      console.error("Error navigating to Final Survey Report:", error);
      setDownloadStatus("error");
      setTimeout(() => setDownloadStatus(""), 3000);
    }
  };

  const downloadPSUReport = (claimNum) => {
    const input = document.getElementById("psu-report");
    if (!input) {
      console.error("PSU Report element not found!");
      return;
    }

    html2canvas(input, { scale: 1 }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
      pdf.save(`PSU-Report-${claimNum}.pdf`);
    });
  };

  const handleModalClose = () => setShowModal(false);

  return (
    <div className="container">
      {/* Modal */}
      {showModal && (
        <div className="modal">
          <div className="modal-content">
            <div className="modal-heading">
              <h3>Assessment Mismatch</h3>
            </div>
            <p>Do you want to accept or reject this?</p>
            <button onClick={handleModalClose}>Accept</button>
            <button onClick={handleModalClose}>Reject</button>
          </div>
        </div>
      )}

      {/* Download Status */}
      {downloadStatus && (
        <div className={`download-message ${downloadStatus}`}>
          {downloadStatus === "downloading" && " Downloading PSU Report..."}
          {downloadStatus === "loading" && " Opening Final Survey Report..."}
          {downloadStatus === "success" && " PSU Report downloaded successfully!"}
          {downloadStatus === "error" && " Action failed. Please try again."}
        </div>
      )}

      <h2 className="review-title">Review {claimNumber}</h2>

      <div className="content-wrapper">
        {/* Left Card */}
        <div className="card">
          <div className="review-box">
            <div className="policy-details">
              <span><strong>Policy:</strong> {claim?.policyNumber || "N/A"}</span>
              <span><strong>Date:</strong> {claim?.date || "N/A"}</span>
              <span><strong>Vehicle:</strong> {claim?.vehicle || "N/A"}</span>
              <span><strong>Model:</strong> {claim?.model || "N/A"}</span>
              <span><strong>Make:</strong> {claim?.make || "N/A"}</span>
            </div>

            {/*  Progress Section - original UI but updated logic */}
            <div className="progress">
              <svg width="90" height="60" viewBox="0 0 80 80">
                <circle
                  className="progress-back"
                  cx="40"
                  cy="40"
                  r="30"
                  strokeWidth="6"
                />
                <circle
                  className="progress-front"
                  cx="40"
                  cy="40"
                  r="30"
                  strokeWidth="6"
                  strokeDasharray={2 * Math.PI * 30}
                  strokeDashoffset={
                    2 * Math.PI * 30 - (progressPercent / 100) * (2 * Math.PI * 30)
                  }
                />
                <text
                  x="50%"
                  y="50%"
                  textAnchor="middle"
                  dy=".3em"
                  className="progress-label"
                >
                  {progressLabel}
                </text>
              </svg>
              <div className="progress-details">
                <span className="progress-fm">{claimNumber}</span>
                <span className="progress-text">
                  {getStatusText(currentStep, totalSteps)}
                </span>
              </div>
            </div>

            {/* Report List */}
            <div className="report-list">
              {[
                "Initial Work Report",
                "Final Work Report",
                "Initial Assessment Report",
                "Final Assessment Report",
                "Final Survey Report",
                "PSU Report",
              ].map((report, index) => (
                <div className="report-item" key={index}>
                  <span>{report}</span>
                  <button
                    className={`preview-btn ${
                      report === "PSU Report"
                        ? "psu-preview-btn"
                        : report === "Final Survey Report"
                        ? "final-survey-btn"
                        : ""
                    }`}
                    onClick={() => handlePreviewClick(report)}
                    disabled={
                      (downloadStatus === "downloading" && report === "PSU Report") ||
                      (downloadStatus === "loading" && report === "Final Survey Report")
                    }
                  >
                    {downloadStatus === "downloading" && report === "PSU Report"
                      ? "Downloading..."
                      : downloadStatus === "loading" &&
                        report === "Final Survey Report"
                      ? "Opening..."
                      : "Preview"}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Card */}
        <div className="card-second">
          <div className="mismatch-box">
            {[
              { type: "Assessment Mismatch", amount: "₹15,000" },
              { type: "Invoice Mismatch", amount: "₹12,500" },
              { type: "Assessment Mismatch", amount: "₹10,800" },
              { type: "Invoice Mismatch", amount: "₹8,700" },
            ].map((mismatch, index) => (
              <div className="mismatch-item" key={index}>
                <div className="mismatch-header">
                  <h4>{mismatch.type}</h4>
                </div>
                <div className="mismatch-details">
                  <p>
                    The estimated repair cost provided by the workshop exceeds
                    the assessed value by {mismatch.amount}
                  </p>
                  <button
                    className="preview-btn"
                    onClick={() => handlePreviewClick(mismatch.type)}
                  >
                    Preview
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewComponent;
